import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Layout/Sidebar';
import ClientService from '../../services/clientService';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';

export function ViewClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await ClientService.getClientById(id);
        
        if (response?.data?.client) {
          setClient(response.data.client);
        } else if (response?.client) {
          setClient(response.client);
        } else {
          setError('Client data not found in response');
        }
      } catch (err) {
        console.error('Error fetching client:', err);
        setError('Failed to load client data');
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex justify-between items-center">
              <h1 className="text-3xl font-bold bg-black py-2 px-8 rounded-md">Client Details</h1>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-black"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex justify-between items-center">
              <h1 className="text-3xl font-bold bg-black py-2 px-8 rounded-md">Client Details</h1>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <div className="text-red-500 text-lg font-medium mb-4">
                {error || 'Client not found'}
              </div>
              <button
                onClick={() => navigate('/clients/list')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center mx-auto"
              >
                <FiArrowLeft className="mr-2" />
                Back to Client List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex justify-between items-center">
            <h1 className="text-md sm:text-3xl font-bold text-white bg-black py-1 sm:py-2 px-4 sm:px-8 rounded-md m-1">Client Details</h1>
            <button
              onClick={() => navigate('/clients/list')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <FiArrowLeft className="mr-2" />
              Back to List
            </button>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FiUser className="mr-2 text-indigo-600" />
                Personal Information
              </h2>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-500">Client ID</h3>
                <p className="text-lg font-semibold text-gray-800">{client.id}</p>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-500">Client Name</h3>
                <p className="text-lg font-semibold text-gray-800">{client.name}</p>
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-500">Company name</h3>
                <p className="text-lg font-semibold text-gray-800">{client.company}</p>
              </div> 

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <FiMail className="mr-1" />
                  Email Address
                </h3>
                <p className="text-lg font-semibold text-gray-800">{client.email}</p>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <FiPhone className="mr-1" />
                  Phone Number
                </h3>
                <p className="text-lg font-semibold text-gray-800">{client.phone}</p>
              </div>
              
              <div className="space-y-1 md:col-span-2">
                <h3 className="text-sm font-medium text-gray-500 flex items-center">
                  <FiMapPin className="mr-1" />
                  Address
                </h3>
                <p className="text-lg font-semibold text-gray-800">{client.address}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewClient;